import { ImageIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/*
 * IDE configuration screenshots.
 *
 * Drop image files into:
 *   src/assets/docs/ide/<tool>/<order>-<optional-name>.<png|jpg|jpeg|webp>
 *
 * e.g. src/assets/docs/ide/cursor/1-models.png
 *      src/assets/docs/ide/cursor/2-endpoint.png
 *
 * Files are picked up automatically and rendered in ascending numeric order on
 * the matching IDE page. <tool> must match the `tool` prop passed below.
 */

type ImageModule = { default: string };

const imageModules = import.meta.glob<ImageModule>(
  '../../assets/docs/ide/**/*.{png,jpg,jpeg,webp}',
  { eager: true },
);

interface Screenshot {
  tool: string;
  order: number;
  name: string;
  src: string;
}

const screenshots: Screenshot[] = Object.entries(imageModules)
  .map(([path, module]) => {
    const match = path.match(/\/ide\/([^/]+)\/([^/]+)\.(?:png|jpe?g|webp)$/i)!;
    const [, tool, file] = match;
    const order = Number.parseInt(file, 10);
    return {
      tool,
      order: Number.isNaN(order) ? Number.MAX_SAFE_INTEGER : order,
      name: file,
      src: module.default,
    };
  })
  .sort((a, b) => a.order - b.order || a.name.localeCompare(b.name));

export function DocScreenshots({ tool }: { tool: string }) {
  const { t } = useTranslation('docs');
  const items = screenshots.filter((shot) => shot.tool === tool);

  if (items.length === 0) {
    return null;
  }

  return (
    <section className="space-y-6">
      <div className="flex items-center gap-2 text-primary">
        <ImageIcon className="h-5 w-5" />
        <h3 className="text-2xl font-semibold text-foreground">{t('ide.screenshots')}</h3>
      </div>
      <div className="grid gap-6 md:grid-cols-2">
        {items.map((shot) => (
          <a
            key={shot.src}
            href={shot.src}
            target="_blank"
            rel="noreferrer"
            className="block overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition hover:border-indigo-300 hover:shadow-md"
          >
            <img
              src={shot.src}
              alt={`${tool} ${shot.name}`}
              className="w-full object-contain"
              loading="lazy"
            />
          </a>
        ))}
      </div>
    </section>
  );
}
